import type { ReactNode } from 'react';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  ChevronRight,
  CircleDollarSign,
  Home,
  LandPlot,
  Leaf,
  Map,
  Target,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { tipoLabel, type DashboardExecutive } from '../schemas';

// Dashboard executivo — visão macro para diretoria. Dinheiro primeiro (receita +
// comercial), depois operação por área, carteira e alertas de gestão. Light/dark
// saem dos tokens (bg-elevated + border-border = mesmo tratamento dos cards CRM).

const NEUTRAL_SEG = 'rgba(140,135,128,0.45)'; // segmento "outros" / trilho — neutro nos 2 temas
const TRACK = 'rgba(140,135,128,0.18)';
const CORAL_TONES = ['#e96a45', '#ef8468', '#f2977f', '#f5ac98', '#f8c3b4'];

function fmtCompactBRL(v: number): string {
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
  if (v >= 1_000) return `R$ ${(v / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
  return `R$ ${v.toLocaleString('pt-BR')}`;
}
const fmtInt = (v: number) => v.toLocaleString('pt-BR');

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">{children}</p>;
}

function Sparkline({ values }: { values: number[] }) {
  if (!values || values.length < 2) return <span className="h-[26px] w-[58px] shrink-0" />;
  const w = 58, h = 26, pad = 3;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - 2 * pad);
      const y = h - pad - ((v - min) / span) * (h - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" className="shrink-0">
      <polyline points={pts} stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Delta({ pct }: { pct: number }) {
  const pos = pct >= 0;
  const Icon = pos ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
        pos ? 'text-[var(--ok)]' : 'text-danger',
      )}
      style={{ background: pos ? 'rgba(61,143,95,0.12)' : 'rgba(200,60,50,0.12)' }}
    >
      <Icon size={11} strokeWidth={2.5} />
      {pos ? '+' : ''}
      {pct.toFixed(1).replace('.', ',')}%
    </span>
  );
}

function IconBox({ icon: Icon, size = 34 }: { icon: LucideIcon; size?: number }) {
  return (
    <span
      className="flex items-center justify-center rounded-[9px] bg-[var(--accent)]"
      style={{ width: size, height: size }}
    >
      <Icon size={Math.round(size * 0.5)} strokeWidth={1.9} className="text-[var(--on-solid)]" />
    </span>
  );
}

function KpiCard({
  icon,
  label,
  value,
  unit,
  trend,
  delta,
  foot,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  unit?: string;
  trend?: number[];
  delta?: number | null;
  foot?: string;
}) {
  return (
    <div className="flex flex-1 basis-[180px] flex-col gap-2.5 rounded-[14px] border border-transparent bg-elevated p-4">
      <div className="flex items-center justify-between">
        <IconBox icon={icon} size={32} />
        {trend ? <Sparkline values={trend} /> : <span className="h-[26px] w-[58px] shrink-0" />}
      </div>
      <span className="text-[10.5px] font-medium uppercase tracking-[0.04em] text-text-muted">{label}</span>
      <div className="flex items-baseline gap-1">
        <span className="text-[23px] font-bold tracking-tight text-text">{value}</span>
        {unit && <span className="text-xs font-medium text-text-muted">{unit}</span>}
      </div>
      {delta != null ? <Delta pct={delta} /> : foot ? <span className="text-[11.5px] text-text-muted">{foot}</span> : null}
    </div>
  );
}

function StatDot({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: color }} />
      {children}
    </span>
  );
}

const AREA_ICON: Record<string, LucideIcon> = {
  fundiario: Map,
  ambiental: Leaf,
  credito: Banknote,
};

function AreaCard({ label, icon, andamento, concl, atras }: {
  label: string;
  icon: LucideIcon;
  andamento: number;
  concl: number;
  atras: number;
}) {
  return (
    <div className="flex flex-1 basis-[200px] flex-col gap-3 rounded-[14px] border border-transparent bg-elevated p-[18px]">
      <div className="flex items-center gap-2.5">
        <IconBox icon={icon} />
        <span className="text-sm font-semibold text-text">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[25px] font-bold tracking-tight text-text">{fmtInt(andamento)}</span>
        <span className="text-[11.5px] text-text-muted">em andamento</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs font-medium text-text-2">
        <StatDot color="var(--ok)">{concl} concluídos</StatDot>
        <StatDot color="var(--warning)">{atras} atrasados</StatDot>
      </div>
    </div>
  );
}

function CrmCard({ oportunidades, pipeline, ganhos }: { oportunidades: number; pipeline: string; ganhos: number }) {
  return (
    <div className="flex flex-1 basis-[200px] flex-col gap-3 rounded-[14px] border border-transparent bg-elevated p-[18px]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <IconBox icon={Target} />
          <span className="text-sm font-semibold text-text">CRM · Comercial</span>
        </div>
        <span className="rounded-full bg-[var(--accent)] px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-[0.05em] text-[var(--on-solid)]">
          Destaque
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[25px] font-bold tracking-tight text-[var(--accent)]">{fmtInt(oportunidades)}</span>
        <span className="text-[11.5px] text-text-2">oportunidades ativas</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs font-medium text-text-2">
        <StatDot color="var(--accent)">{pipeline} pipeline</StatDot>
        <StatDot color="var(--ok)">{ganhos} ganhos/mês</StatDot>
      </div>
    </div>
  );
}

function MixComposicao({ items }: { items: DashboardExecutive['mix']['por_tipo'] }) {
  const total = items.reduce((s, i) => s + i.count, 0) || 1;
  const top = items.slice(0, 5);
  const outros = items.slice(5).reduce((s, i) => s + i.count, 0);
  const segs = top.map((t, i) => ({
    label: tipoLabel(t.tipo),
    count: t.count,
    pct: Math.round((t.count / total) * 100),
    color: CORAL_TONES[i] ?? CORAL_TONES[CORAL_TONES.length - 1]!,
  }));
  if (outros > 0) segs.push({ label: 'Outros serviços', count: outros, pct: Math.round((outros / total) * 100), color: NEUTRAL_SEG });

  return (
    <div className="flex flex-col gap-[18px] rounded-[14px] border border-transparent bg-elevated p-[22px]">
      <div className="flex flex-col gap-1">
        <SectionLabel>Mix da carteira · por tipo</SectionLabel>
        <p className="text-[13px] text-text-2">Composição dos processos em andamento</p>
      </div>
      <div className="flex h-[18px] w-full gap-[3px] overflow-hidden rounded-full">
        {segs.map((s, i) => (
          <div key={i} className="h-full" style={{ flexGrow: s.count, background: s.color }} title={`${s.label}: ${s.count}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {segs.map((s, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <span className="h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: s.color }} />
            <span className="flex-1 truncate text-[12.5px] font-medium text-text">{s.label}</span>
            <span className="text-[12.5px] font-semibold text-text">{s.count}</span>
            <span className="text-[11.5px] text-text-muted">{s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertasPanel({ alertas }: { alertas: DashboardExecutive['alertas'] }) {
  const color = (s: string) => (s === 'danger' ? 'var(--danger)' : s === 'warning' ? 'var(--warning)' : 'var(--info)');
  return (
    <div className="flex basis-[460px] grow-0 flex-col rounded-[14px] border border-transparent bg-elevated px-[22px] pb-3.5 pt-5 max-md:basis-full">
      <SectionLabel>Requer atenção</SectionLabel>
      <div className="mt-2 flex flex-col">
        {alertas.length === 0 && <p className="py-4 text-sm text-text-muted">Nenhum alerta no momento.</p>}
        {alertas.map((a, i) => (
          <div key={i} className={cn('flex items-center gap-3 py-2.5', i > 0 && 'border-t border-border/70')}>
            <div className="flex flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-2.5">
                <span className="inline-block h-[9px] w-[9px] shrink-0 rounded-full" style={{ background: color(a.severidade) }} />
                <span className="text-[12.5px] font-medium text-text">{a.texto}</span>
              </span>
              <span className="pl-[19px] text-[11px] text-text-muted">{a.contexto}</span>
            </div>
            <ChevronRight size={16} className="shrink-0 text-text-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CidadeRanking({ items }: { items: DashboardExecutive['mix']['por_cidade'] }) {
  const top = items.slice(0, 7);
  const max = Math.max(...top.map((c) => c.count), 1);
  return (
    <div className="flex flex-1 basis-[320px] flex-col gap-4 rounded-[14px] border border-transparent bg-elevated p-[22px]">
      <SectionLabel>Distribuição por cidade</SectionLabel>
      <div className="flex flex-col gap-3.5">
        {top.map((c, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-3.5 text-[11.5px] font-semibold text-text-muted">{i + 1}</span>
            <span className="w-[140px] shrink-0 truncate text-[12.5px] font-medium text-text">{c.cidade}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: TRACK }}>
              <span className="block h-full rounded-full bg-[var(--accent)]" style={{ width: `${(c.count / max) * 100}%` }} />
            </span>
            <span className="w-9 text-right text-[12.5px] font-semibold text-text">{c.count}</span>
            <span className="w-[80px] text-right text-[11.5px] font-medium text-text-2">{fmtInt(c.hectares)} ha</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface ExecDashboardProps {
  data: DashboardExecutive;
  receitaFechada: number;
  negociosFechados: number;
  oportunidadesAtivas: number;
  pipelineBruto: number;
  ganhosMes: number;
}

export function ExecDashboard({
  data,
  receitaFechada,
  negociosFechados,
  oportunidadesAtivas,
  pipelineBruto,
  ganhosMes,
}: ExecDashboardProps) {
  const { carteira, operacao_por_area, alertas, mix } = data;
  const trend = carteira.trend_12m;
  const series = (key: 'clientes' | 'imoveis' | 'processos' | 'hectares') => trend.map((t) => Number(t[key]));
  const delta = (key: 'clientes' | 'imoveis' | 'processos' | 'hectares'): number | null => {
    if (trend.length < 2) return null;
    const last = Number(trend[trend.length - 1]?.[key] ?? 0);
    const prev = Number(trend[trend.length - 2]?.[key] ?? 0);
    if (prev === 0) return null;
    return ((last - prev) / prev) * 100;
  };

  return (
    <div className="flex flex-col gap-[22px]">
      {/* KPIs primários */}
      <div className="flex flex-wrap gap-3.5">
        <KpiCard
          icon={CircleDollarSign}
          label="Receita fechada · mês"
          value={fmtCompactBRL(receitaFechada)}
          foot={`${negociosFechados} negócios fechados`}
        />
        <KpiCard icon={Users} label="Clientes ativos" value={fmtInt(carteira.clientes_ativos)} trend={series('clientes')} delta={delta('clientes')} />
        <KpiCard icon={Home} label="Imóveis sob gestão" value={fmtInt(carteira.imoveis)} trend={series('imoveis')} delta={delta('imoveis')} />
        <KpiCard icon={LandPlot} label="Hectares sob gestão" value={fmtInt(Math.round(carteira.hectares_sob_gestao))} unit="ha" trend={series('hectares')} delta={delta('hectares')} />
        <KpiCard icon={Activity} label="Processos em andamento" value={fmtInt(carteira.processos_em_andamento)} trend={series('processos')} delta={delta('processos')} />
      </div>

      {/* Operação por área */}
      <section className="flex flex-col gap-3.5">
        <SectionLabel>Operação por área</SectionLabel>
        <div className="flex flex-wrap gap-4">
          {operacao_por_area.map((a) => (
            <AreaCard key={a.dominio} label={a.label} icon={AREA_ICON[a.dominio] ?? Activity} andamento={a.em_andamento} concl={a.concluidos_mes} atras={a.atrasados} />
          ))}
          <CrmCard oportunidades={oportunidadesAtivas} pipeline={fmtCompactBRL(pipelineBruto)} ganhos={ganhosMes} />
        </div>
      </section>

      {/* Mix da carteira */}
      <MixComposicao items={mix.por_tipo} />

      {/* Alertas + Distribuição por cidade */}
      <div className="flex flex-wrap items-stretch gap-4">
        <AlertasPanel alertas={alertas} />
        <CidadeRanking items={mix.por_cidade} />
      </div>
    </div>
  );
}
